(ns manage.adset-template-page.view.templates-selection
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Link$default" :as Link]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium isAutogeneratedRow]]
            [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.queries :refer [use-query]]
            [common.resource.query.adset :as adset-queries]
            [common.resource.query.template :as template.q]
            [common.util :refer [format-timestamp spread-obj]]
            [common.widgets.column-renderers :refer [template-duration-renderer]]
            [common.widgets.data-grid :as dg]
            [common.widgets.input :refer [input]]
            [common.widgets.status :as status]
            [goog.object :as gobject]
            [manage.adset.hooks :as ah]
            [manage.template-table.db :refer [after-effects-templates?]]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as r.easy]
            [uix.core :refer [$ defui] :as uix]
            [uix.re-frame :refer [use-subscribe]]))

(def add-template-warning-props
  {:title "Adding Templates to exported Adsets"
   :body "You are adding a Template to an exported Adset. This change might require you to export the Adset again to a third-party platform."
   :submit-button-label "Add Templates"})

(def modify-template-warning-props
  {:title "Changing Templates in exported Adsets"
   :body "You are changing a Template in an exported Adset. This change might require you to export the Adset again to a third-party platform."
   :submit-button-label "Change Template"})

(def delete-template-warning-props
  {:title "Delete Templates from exported Adsets"
   :body "You are deleting a Template from an exported Adset. This change might require you to export the Adset again to a third-party platform."
   :submit-button-label "Delete Template"})

(defn allow-multiple-templates-selection?
  "Only a single after effects template can be selected as of now."
  [channel]
  (not (after-effects-templates? channel)))

(defn render-dimensions-cell [{:keys [width height]}]
  (if-not (or (str/blank? width) (str/blank? height))
    (str width "x" height)
    ""))

(defn render-audit-state-cell [row]
  (when-not (:asset-tree/id row)
    (let [audit-state (:auditState row)
          mapping {"in progress" ["running" "grey.40"]
                   "passed" ["successful" "success.main"]
                   "failed" ["failed" "error.main"]
                   "errors" ["failed" "error.main"]
                   nil ["not applicable" "grey.30"]}
          [text color] (get mapping audit-state)]
      ($ status/simple {:color color :status text}))))

(defn- columns-data [after-effects-templates?]
  (->> [(when after-effects-templates?
          {:field "audit"
           :headerName "Audit"
           :type "string"
           :minWidth 100
           :flex 1
           :valueGetter (fn [_value row ^js _column ^js _apiRef]
                          (:latestRevision row))
           :renderCell (fn [^js params]
                         (when-let [latest-revision (.-value params)]
                           (render-audit-state-cell latest-revision)))})

        (when after-effects-templates?
          {:field "duration"
           :headerName "Duration"
           :type "string"
           :width 80
           :valueGetter (fn [_value row ^js _column ^js _apiRef]
                          (get-in row [:revisions 0 :duration]))
           :renderCell (fn [^js params]
                         (template-duration-renderer (.-value params)))})

        {:field "Dimensions"
         :flex 1
         :valueGetter (fn [_value row ^js _column ^js _apiRef]
                        (:latestRevision row))
         :renderCell (fn [^js params]
                       (let [latest-revision (.-value params)]
                         (render-dimensions-cell latest-revision)))}

        {:field "updated-at"
         :headerName "Modified"
         :type "string"
         :minWidth 100
         :flex 1
         :valueGetter (fn [_value row ^js _column ^js _apiRef]
                        (:updatedAt row))
         :renderCell (fn [^js params]
                       (when-let [updated-at (.-value params)]
                         (format-timestamp updated-at)))}]
       (filter some?)
       clj->js))

(defn folder-name-by-id [folder-id]
  (let [folders @(rf/subscribe [:cqrs/value :asset/list-folders])
        template-folders (->> folders
                              :children
                              (filter (fn [folder]
                                        (= (:asset-tree/name folder) "Templates")))
                              first)]
    (cond
      (= folder-id (:asset-tree/id template-folders)) "Templates"
      (= folder-id template.q/pre-made-template-folder-id) "Pre-made templates"
      :else (->> template-folders
                 :children
                 (filter #(= (:asset-tree/id %) folder-id))
                 first
                 :asset-tree/name))))

(defn- get-row-id [row]
  (:id row))

(defn- get-folder [row]
  (or (clj->js (conj (:folder-hierarchy row) (:id row)))
      #js [(:id row)]))

(defn get-row-node-using-a-hack [row ^js apiRef]
  (let [;; GRID_ID_AUTOGENERATED is part of MUI internals, was not meant to be accessed.
        ;; But we need to use it, so here we go with this very dirty JS hack.
        GRID_ID_AUTOGENERATED (first (js/Object.getOwnPropertySymbols row))
        id (gobject/get row GRID_ID_AUTOGENERATED)
        ^js api (.-current apiRef)]
    (.getRowNode api id)))

(def grouping-col-def
  #js {:hideDescendantCount true
       :headerName "Name"
       :minWidth 200
       :flex 2
       :valueGetter (fn [_value row ^js _column ^js apiRef]
                      (if (isAutogeneratedRow row)
                        ($ Stack
                           {:direction "row"
                            :alignItems "center"
                            :gap 1}
                           ($ Icon "folder")
                           (let [rowNode (get-row-node-using-a-hack row apiRef)]
                             (.-groupingKey rowNode)))
                        (:name row)))})

(defn build-folder-hierarchy
  [template folders hierarchy]
  (when (:folderId template)
    (let [children (:children folders)
          template-folder (when (= (:asset-tree/id folders) (:folderId template))
                            folders)
          pre-made-template-folder (when (= template.q/pre-made-template-folder-id (:folderId template))
                                     "Pre-made templates")]
      (cond
        template-folder (-> hierarchy
                            (conj (:asset-tree/name template-folder)))
        pre-made-template-folder (-> hierarchy
                                     (conj "Pre-made templates"))
        children (some #(build-folder-hierarchy template % (conj hierarchy (:asset-tree/name folders))) children)
        :else nil))))

(defui templates-selection-table
  [{:keys [qualify multiselect-enabled? select-fn is-row-selectable channel]}]
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {state :state all-templates :value} (use-subscribe [(qualify :unpaginated-value)])
        {pre-made-templates :data} (use-query (template.q/pre-made-templates-query {:channel channel}))
        all-templates* (flatten (conj all-templates (or pre-made-templates [])))
        template-id->template (mc/index-by :id all-templates*)
        {page-size :size} (use-subscribe [(qualify :page)])

        {{:keys [assigned-template-ids assigned-template-dimensions]} :data}
        (use-query (spread-obj (adset-queries/adset-template-query {:adset-id adset-id})
                               #js {:select (fn [^js data]
                                              (let [data (->clj data)]
                                                {:assigned-template-ids (set (map :id data))
                                                 :assigned-template-dimensions (->> data
                                                                                    (mapcat :revisions)
                                                                                    (map (juxt :width :height))
                                                                                    (into #{}))}))}))

        {:keys [workflow]} (ah/use-adset)
        rules-workflow? (= workflow "rules")

        search (use-subscribe [(qualify :search-term)])
        selected-rows (use-subscribe [(qualify :selected-rows)])
        newly-selected-template-dimensions (set
                                            (for [template-id selected-rows
                                                  :let [{{:keys [width height]} :latestRevision :as _selected-template} (get template-id->template template-id)]]
                                              [width height]))
        no-rows-label (cond
                        (= :failure state) "Could not load Templates"
                        (= :pending state) "Loading Templates"
                        (and (not-empty search)
                             (empty? all-templates*)) "No Templates found for this term"
                        (empty? all-templates*) "No Templates have been created yet"
                        :else "")
        folders (use-subscribe [:cqrs/value :asset/list-folders])
        template-folders (->> folders
                              :children
                              (filter (fn [folder]
                                        (= (:asset-tree/folder-type folder) "templates")))
                              first)
        ;; DataGrid will reinitialize TreeData if value changes, which would mean that that expanded/collapsed
        ;; state is lost. We need to be careful to not change value* identity when e.g. selection-model changes,
        ;; so we don't lose tree state.
        templates* (uix/use-memo (fn []
                                   (->> all-templates*
                                        (mapv (fn [row]
                                                (-> row
                                                    (assoc :folder-name (or (folder-name-by-id (:folderId row)) "Uncategorised"))
                                                    (assoc :folder-hierarchy (build-folder-hierarchy row template-folders [])))))
                                        to-array))
                                 [all-templates* template-folders])
        find-templates (fn [ids] (->> all-templates* (filter #(contains? ids (:id %)))))
        on-selection #(do (rf/dispatch [(qualify :select-rows) %])
                          (when (some? select-fn)
                            (select-fn (find-templates (set %)))))
        columns (uix/use-memo (fn []
                                (columns-data (after-effects-templates? channel)))
                              [channel])
        on-selection-model-change (fn [^js selection]
                                    (on-selection (js->clj selection)))
        initial-state (uix/use-memo (fn []
                                      #js {:pagination #js {:paginationModel #js {:pageSize page-size}}})
                                    [page-size])
        is-row-selectable* (fn [^js params]
                             (let [template (.-row params)
                                   audit-state (-> template :latestRevision :auditState)
                                   template-dimensions ((juxt :width :height) (:latestRevision template))]
                               ;; A template is selectable if..
                               ;; it is already selected
                               (or (selected-rows (:id template))
                                   ;; OR it has NOT been assigned to the adset previously
                                   (and (not (contains? assigned-template-ids (:id template)))
                                        ;; AND its audit has passed, or it doesn't need to be audited
                                        (contains? #{nil "passed"} audit-state)
                                        ;; AND it has a template id
                                        (:id template)
                                        ;; AND a template with its dimensions has not been assigned previously
                                        (or (and (not (contains? assigned-template-dimensions template-dimensions))
                                                 ;; AND a template with its dimensions has not yet been selected
                                                 (not (contains? newly-selected-template-dimensions template-dimensions)))
                                            ;; OR it is a rule-based workflow adset, which could use same dimensions templates
                                            rules-workflow?)))))]
    ($ DataGridPremium
       {:columns columns

        :loading (= state :pending)
        :localeText #js {:noRowsLabel no-rows-label}
        :getRowClassName (fn [^js params]
                           (when (and (not ((or is-row-selectable is-row-selectable*) params))
                                      (-> (.-row params) :latestRevision)) "greyed-out"))

        :rows templates*
        :getRowId get-row-id

        :treeData true
        :getTreeDataPath get-folder
        :groupingColDef grouping-col-def
        :defaultGroupingExpansionDepth -1

        :checkboxSelection multiselect-enabled?
        :isRowSelectable (or is-row-selectable is-row-selectable*)

        :disableMultipleRowSelection (not multiselect-enabled?)
        :keepNonExistentRowsSelected true
        :rowSelectionModel (clj->js selected-rows)
        :onRowSelectionModelChange on-selection-model-change

        :pagination true
        :pageSizeOptions #js [page-size]

        :initialState initial-state

        :slots #js {:row dg/WithDynamicPropsRow
                    :cell dg/WithDynamicPropsCell
                    :treeDataExpandIcon (uix/as-react
                                         (fn []
                                           ;; Used to check if clicking expand/collapse icon directly
                                           ($ Icon {:data-expand 1} "chevron_right")))
                    :treeDataCollapseIcon (uix/as-react
                                           (fn []
                                             ($ Icon {:data-expand 1} "expand_more")))}
        :slotProps #js {:row #js {:get-props-fn (fn [props]
                                                  {:data-testid (-> props :row :name)})}
                        :cell #js {:get-props-fn (fn [{:keys [^js column]}]
                                                   (let [field (.-field column)
                                                         field (if (= field "__tree_data_group__") "name" field)]
                                                     {:data-testid field}))}}

        :noWrap true
        :sx #js {".MuiDataGrid-treeDataGroupingCellToggle" #js {:mr 0}
                 ".greyed-out" #js {:opacity 0.45}
                 ".MuiDataGrid-row:not(.greyed-out)" #js {:cursor "pointer"}}})))

(defui filter-input
  [{:keys [qualify]}]
  ($ input
     {:sx #js {:maxWidth "350px"}
      :value (use-subscribe [(qualify :search-term)])
      :onChange #(do (rf/dispatch [(qualify :search-term) (-> % .-target .-value)])
                     (rf/dispatch [(qualify :confirm-search)]))
      :placeholder "Search by name"
      :data-testid :search-by-name-input}))

(defui audit-alert
  [{:keys [channel]}]
  (let [[message-hidden? set-message-hidden] (uix/use-state false)
        url-context (use-subscribe [:url-context])]
    (when (and (not message-hidden?) (after-effects-templates? channel))
      ($ Alert
         {:severity "info"
          :onClose #(set-message-hidden true)
          :sx #js {:mx 2
                   :mb 1}}
         "Only successfully audited templates can be assigned to an adset. Go to "
         ($ Link
            {:href (r.easy/href :advertiser/templates url-context)}
            "Template library")
         " for more details."))))
